import {useData} from "@hps/hops-react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import RaildaysFeedbackBanner from "Raildays/Components/RaildaysFeedbackBanner.js";
import RaildaysMap from "Raildays/Components/RaildaysMap.js";
import {
	resolveExternalUrl,
	resolveRailwayLocation,
	resolveRailwayMediaUrl,
	resolveRailwayType,
	resolveRailwayTypeColor,
	slugify
} from "Raildays/RaildaysUtils.js";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useParams, useSearchParams} from "react-router-dom";

import Button from "Components/Button.js";
import Card from "Components/Card.js";
import CardSection from "Components/Container.js";
import Dialog from "Components/Dialog.js";
import IconButton from "Components/IconButton.js";
import Image from "Components/Image.js";
import Link from "Components/Link.js";
import Loader from "Components/Loader.js";
import RaildaysService from "Services/RaildaysService.js";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import XIcon from "@mui/icons-material/X";

const CARD_BODY_PADDING = "clamp(16px, 2.2vw, 32px)";

const resolveSocialUrl = (base, handle) => {
	if (!handle) return "";
	if (handle.startsWith("http")) return handle;
	return `${base}${handle.replace(/^@/, "")}`;
};

const getContrastText = hexColor => {
	if (!hexColor) return "#111827";
	const color = hexColor.replace("#", "");
	const r = parseInt(color.substring(0, 2), 16) || 0;
	const g = parseInt(color.substring(2, 4), 16) || 0;
	const b = parseInt(color.substring(4, 6), 16) || 0;
	const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
	return luminance > 0.7 ? "#111827" : "#fff";
};

const buildMonthGrid = monthDate => {
	const startOfMonth = monthDate.startOf("month");
	const endOfMonth = monthDate.endOf("month");
	const startDay = startOfMonth.day();
	const daysInMonth = endOfMonth.date();
	const weeks = [];
	let current = startOfMonth.subtract(startDay, "day");

	while (current.isBefore(endOfMonth) || current.day() !== 0) {
		const week = [];
		for (let i = 0; i < 7; i += 1) {
			week.push(current);
			current = current.add(1, "day");
		}
		weeks.push(week);
		if (current.date() > daysInMonth && current.day() === 0) break;
	}

	return weeks;
};

const expandDateRange = (startDate, endDate) => {
	if (!startDate) return [];
	const start = dayjs(startDate);
	const end = endDate ? dayjs(endDate) : start;
	const dates = [];
	let cursor = start;
	while (cursor.isBefore(end) || cursor.isSame(end, "day")) {
		dates.push(cursor.format("YYYY-MM-DD"));
		cursor = cursor.add(1, "day");
	}
	return dates;
};

const CalendarView = ({entries, monthLabel, onPrev, onNext}) => {
	const weeks = buildMonthGrid(dayjs(monthLabel));
	const entriesByDate = entries.reduce((acc, entry) => {
		if (!acc[entry.date]) acc[entry.date] = [];
		acc[entry.date].push(entry);
		return acc;
	}, {});

	return (
		<Box>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 2}}>
				<Button label="Prev" onClick={onPrev} variant="outlined" />
				<Typography variant="h6" sx={{fontWeight: 700}}>
					{dayjs(monthLabel).format("MMMM YYYY")}
				</Typography>
				<Button label="Next" onClick={onNext} variant="outlined" />
			</Stack>
			<Box sx={{display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1}}>
				{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(label => (
					<Typography key={label} variant="caption" align="center" color="text.secondary" sx={{fontWeight: 700}}>
						{label}
					</Typography>
				))}
				{weeks.map(week => week.map(day => {
					const isCurrentMonth = day.month() === dayjs(monthLabel).month();
					const dateKey = day.format("YYYY-MM-DD");
					const dayEntries = entriesByDate[dateKey] || [];
					return (
						<Box
							key={dateKey}
							sx={{
								minHeight: 72,
								borderRadius: 1,
								border: "1px solid #e5e7eb",
								backgroundColor: isCurrentMonth ? "#fff" : "#f8fafc",
								p: 1
							}}>
							<Typography variant="caption" color={isCurrentMonth ? "text.primary" : "text.secondary"}>
								{day.date()}
							</Typography>
							<Stack spacing={0.5} sx={{mt: 0.5}}>
								{dayEntries.slice(0, 2).map(entry => (
									<Box
										key={`${entry.id}-${entry.title}`}
										component={entry.onClick ? "button" : "div"}
										onClick={entry.onClick}
										sx={{
											textAlign: "left",
											backgroundColor: entry.background || "#e2e8f0",
											color: entry.color || "#111827",
											borderRadius: 0.5,
											px: 0.5,
											py: 0.25,
											fontSize: "0.7rem",
											border: "none",
											cursor: entry.onClick ? "pointer" : "default"
										}}>
										{entry.title}
									</Box>
								))}
								{dayEntries.length > 2 && (
									<Typography variant="caption" color="text.secondary">
										+{dayEntries.length - 2} more
									</Typography>
								)}
							</Stack>
						</Box>
					);
				}))}
			</Box>
		</Box>
	);
};

const RailwayDetailView = () => {
	const {slug} = useParams();
	const [searchParams] = useSearchParams();
	const mapAnchorRef = useRef(null);
	const [eventsMonth, setEventsMonth] = useState(dayjs().startOf("month"));
	const [timetableMonth, setTimetableMonth] = useState(dayjs().startOf("month"));
	const [activeTimetable, setActiveTimetable] = useState(null);
	const [activeTimetableLoadError, setActiveTimetableLoadError] = useState(false);

	const {data: railwaysData, error: railwaysError, loading: railwaysLoading} = useData(
		useCallback(() => RaildaysService.getRailways(), [])
	);

	const railways = useMemo(() => {
		if (!railwaysData) return [];
		if (Array.isArray(railwaysData)) return railwaysData;
		if (Array.isArray(railwaysData.objects)) return railwaysData.objects;
		return [];
	}, [railwaysData]);

	const railway = useMemo(() => (
		railways.find(item => slugify(item?.Name || "") === slug)
	), [railways, slug]);

	const railwayId = railway?.Id;

	const {data: eventsData, error: eventsError} = useData(useCallback(
		() => (railwayId ? RaildaysService.getRailwayEvents(railwayId) : Promise.resolve([])),
		[railwayId]
	));

	const {data: timetablesData, error: timetablesError} = useData(useCallback(
		() => (railwayId ? RaildaysService.getRailwayTimetables(railwayId) : Promise.resolve(null)),
		[railwayId]
	));

	const name = railway?.Name || "";
	const hero = resolveRailwayMediaUrl(railway?.Hero || railway?.Image);
	const logo = resolveRailwayMediaUrl(railway?.Logo);
	const intro = railway?.Intro || "";
	const description = railway?.Description || "";
	const locationLabel = resolveRailwayLocation(railway);
	const type = resolveRailwayType(railway);
	const address = railway?.Address;
	const website = resolveExternalUrl(railway?.Website);
	const phone = railway?.Phone;
	const tickets = resolveExternalUrl(railway?.TicketsUrl);
	const memberships = resolveExternalUrl(railway?.MembershipsUrl);
	const facebook = resolveSocialUrl("https://facebook.com/", railway?.Facebook);
	const twitter = resolveSocialUrl("https://twitter.com/", railway?.Twitter);
	const instagram = resolveSocialUrl("https://instagram.com/", railway?.Instagram);
	const mapLocation = railway?.Location;

	const events = useMemo(() => (Array.isArray(eventsData) ? eventsData : []), [eventsData]);

	const eventEntries = useMemo(() => (
		events.flatMap(event => {
			const startDate = event?.StartDate || event?.Start;
			const endDate = event?.EndDate || event?.End;
			const dates = expandDateRange(startDate, endDate);
			const title = event?.Name || "Event";
			const id = event?.EventId || event?.Id || title;
			return dates.map(date => ({
				id,
				date,
				title
			}));
		})
	), [events]);

	const timetableEntries = useMemo(() => {
		const days = timetablesData?.Days || [];
		const timetablesCollection = timetablesData?.Timetables || {};
		const timetablesById = new Map();

		if (Array.isArray(timetablesCollection)) {
			timetablesCollection.forEach(item => {
				const id = item?.Id;
				if (id !== undefined && id !== null) timetablesById.set(String(id), item);
			});
		}
		else if (timetablesCollection && typeof timetablesCollection === "object") {
			Object.entries(timetablesCollection).forEach(([key, value]) => {
				timetablesById.set(String(key), value);
				const id = value?.Id;
				if (id !== undefined && id !== null) timetablesById.set(String(id), value);
			});
		}

		return days.map(day => {
			const timetable = day?.Timetable;
			const timetableId = timetable?.Id;
			const detail = (timetableId !== undefined && timetableId !== null) ?
				(timetablesById.get(String(timetableId)) || timetable) :
				timetable;
			const dayDate = day?.Date;
			const imagePath = detail?.Image || timetable?.Image;
			if (!dayDate || !detail) return null;
			return {
				id: timetableId || dayDate,
				date: dayDate,
				title: detail?.Name || "Timetable",
				background: detail?.BackgroundColour || "#fde68a",
				color: detail?.ForegroundColour || "#111827",
				image: resolveRailwayMediaUrl(imagePath)
			};
		}).filter(Boolean);
	}, [timetablesData]);

	const mapMarkers = useMemo(() => {
		const latitude = Number(mapLocation?.Latitude);
		const longitude = Number(mapLocation?.Longitude);
		if (!name || Number.isNaN(latitude) || Number.isNaN(longitude)) return [];
		return [{
			id: railway?.Id || slug,
			name,
			railwayName: name,
			location: locationLabel,
			subname: locationLabel,
			typeLabel: type,
			position: [latitude, longitude],
			color: resolveRailwayTypeColor(railway),
			image: logo,
			actionLabel: "View railway",
			link: `/railways/${slug}`
		}];
	}, [railway, name, logo, locationLabel, mapLocation, slug, type]);

	const handleScrollToMap = () => {
		if (mapAnchorRef.current) {
			mapAnchorRef.current.scrollIntoView({behavior: "smooth", block: "start"});
		}
	};

	const handleScrollToSection = id => {
		if (!id) return;
		const target = document.getElementById(id);
		if (target) {
			target.scrollIntoView({behavior: "smooth", block: "start"});
		}
	};

	const handleCloseActiveTimetable = useCallback(() => {
		setActiveTimetable(null);
	}, []);

	useEffect(() => {
		setActiveTimetableLoadError(false);
	}, [activeTimetable?.image]);

	return (
		<Box
			component="main"
			sx={{
				flexGrow: 1,
				mx: {xs: 2, md: 3, lg: 5},
				mb: {xs: 2, md: 3, lg: 5},
				backgroundColor: "#fff",
				borderRadius: 2
			}}>
			<RaildaysFeedbackBanner show={searchParams.has("feedback")} />

			<Container maxWidth={false} sx={{py: {xs: 4, md: 6}}}>
				{railwaysError && (
					<Alert severity="error" sx={{mb: 3}}>
						We could not load this railway right now. Please try again shortly.
					</Alert>
				)}

				{railwaysLoading && (
					<Stack alignItems="center" sx={{py: 8}}>
						<Loader />
					</Stack>
				)}

				{(!railwaysLoading && !railway) && (
					<Box sx={{backgroundColor: "#fff", borderRadius: 2, p: {xs: 3, md: 4}}}>
						<Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
							Railway not found
						</Typography>
						<Typography color="text.secondary" sx={{mb: 3}}>
							We could not locate that railway. Please return to the list and try again.
						</Typography>
						<Button
							label="Back to railways"
							startIcon={<ArrowBackIcon />}
							uri="/railways"
							variant="text" />
					</Box>
				)}

				{railway && (
					<Stack spacing={2} sx={{px: {xs: 2, md: 4, lg: 6}}}>
						<Box>
							<Breadcrumbs sx={{textTransform: "uppercase", color: "#e2b534"}}>
								<Button label="Home" uri="/" style={{color: "#e2b534"}} variant="text" />
								<Button label="Railways" uri="/railways" style={{color: "#e2b534"}} variant="text" />
								<Typography color="#e2b534">{name}</Typography>
							</Breadcrumbs>
							<Typography variant="h4" sx={{mt: 2, fontWeight: 700, textTransform: "uppercase"}}>
								{name}
							</Typography>
						</Box>

						<Card
							px={0}
							py={0}
							style={{
								backgroundColor: "#fff",
								borderRadius: 12,
								boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
								overflow: "hidden"
							}}>
							{hero && (
								<Image
									alt={name}
									noError={true}
									noLoader={true}
									src={hero}
									style={{width: "100%", aspectRatio: "16 / 9"}}
									imgStyles={{width: "100%", height: "100%", display: "block"}} />
							)}
							<CardSection
								display="block"
								px={0}
								py={0}
								style={{padding: CARD_BODY_PADDING}}>
								<Stack direction={{xs: "column", md: "row"}} spacing={2} alignItems={{xs: "flex-start", md: "center"}} justifyContent="space-between">
									<Stack direction="row" spacing={2} alignItems="center">
										{logo && (
											<Box component="img" src={logo} alt="" sx={{height: 40}} />
										)}
										<Typography variant="h5" sx={{fontWeight: 800, color: "#e2b534"}}>
											{name}
										</Typography>
									</Stack>
									<Button label="View on Map" variant="outlined" onClick={handleScrollToMap} />
								</Stack>

								<Stack spacing={2} sx={{mt: 3}}>
									{intro && <Typography>{intro}</Typography>}
									{description && <Typography color="text.secondary">{description}</Typography>}
								</Stack>

								<Stack direction={{xs: "column", md: "row"}} spacing={2} justifyContent="space-between" sx={{mt: 3}}>
									<Stack direction={{xs: "column", md: "row"}} spacing={2}>
										<Button label="View Events" variant="outlined" onClick={() => handleScrollToSection("events")} />
										<Button label="View FAQs" variant="outlined" uri={`/railways/${slug}/faqs`} />
										{tickets && (
											<Button label="Buy Tickets" variant="outlined" onClick={() => window.open(tickets, "_blank")} />
										)}
									</Stack>
									<Stack direction="row" spacing={1} alignItems="center">
										{twitter && (
											<Link uri={twitter}>
												<IconButton
													icon={XIcon}
													label="X"
													style={{border: "1px solid #e2b534", color: "#e2b534"}} />
											</Link>
										)}
										{facebook && (
											<Link uri={facebook}>
												<IconButton
													icon={FacebookIcon}
													label="Facebook"
													style={{border: "1px solid #e2b534", color: "#e2b534"}} />
											</Link>
										)}
										{instagram && (
											<Link uri={instagram}>
												<IconButton
													icon={InstagramIcon}
													label="Instagram"
													style={{border: "1px solid #e2b534", color: "#e2b534"}} />
											</Link>
										)}
									</Stack>
								</Stack>
							</CardSection>
						</Card>

						<Card px={0} py={0} style={{borderRadius: 12, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)"}}>
							<CardSection
								display="block"
								px={0}
								py={0}
								style={{padding: CARD_BODY_PADDING}}>
								<Stack spacing={2}>
									<Stack direction="row" spacing={1} alignItems="center">
										<Typography variant="subtitle1" sx={{fontWeight: 700, minWidth: 120}}>
											Location
										</Typography>
										<Typography>
											{address?.Address || ""}
											{address?.City ? ", " : ""}
											{address?.City || ""}
											{address?.County ? ", " : ""}
											{address?.County || ""}
											{address?.Postcode ? ", " : ""}
											{address?.Postcode || ""}
											{address?.Country ? ", " : ""}
											{address?.Country || ""}
										</Typography>
									</Stack>

									{type && (
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography variant="subtitle1" sx={{fontWeight: 700, minWidth: 120}}>
												Railway Type
											</Typography>
											<Chip
												label={type}
												sx={{
													backgroundColor: resolveRailwayTypeColor(railway),
													color: getContrastText(resolveRailwayTypeColor(railway))
												}} />
										</Stack>
									)}

									{website && (
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography variant="subtitle1" sx={{fontWeight: 700, minWidth: 120}}>
												Website
											</Typography>
											<Button
												label={website}
												variant="text"
												onClick={() => window.open(website, "_blank")} />
										</Stack>
									)}

									{phone && (
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography variant="subtitle1" sx={{fontWeight: 700, minWidth: 120}}>
												Phone
											</Typography>
											<Typography>{phone}</Typography>
										</Stack>
									)}

									{memberships && (
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography variant="subtitle1" sx={{fontWeight: 700, minWidth: 120}}>
												Memberships
											</Typography>
											<Button
												label="View memberships"
												variant="text"
												onClick={() => window.open(memberships, "_blank")} />
										</Stack>
									)}
								</Stack>
							</CardSection>
						</Card>

						<Box id="events" />
						<Card px={0} py={0} style={{borderRadius: 12, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)"}}>
							<CardSection
								display="block"
								px={0}
								py={0}
								style={{padding: CARD_BODY_PADDING}}>
								<Stack spacing={3}>
									<Typography variant="h5" sx={{fontWeight: 800, color: "#e2b534"}}>
										Events at {name}
									</Typography>
									{eventsError && (
										<Alert severity="error">We could not load events right now.</Alert>
									)}
									{events.length ?
										(
											<CalendarView
												entries={eventEntries}
												monthLabel={eventsMonth}
												onPrev={() => setEventsMonth(prev => prev.subtract(1, "month"))}
												onNext={() => setEventsMonth(prev => prev.add(1, "month"))}
											/>
										) :
										(
											<Typography color="text.secondary">
											There are no events found for {name}.
											</Typography>
										)}
									{events.length > 0 && (
										<Grid container spacing={2}>
											{events.map(event => {
												const eventName = event?.Name || "Event";
												const eventImage = resolveRailwayMediaUrl(event?.Image);
												const startDate = event?.StartDate;
												const endDate = event?.EndDate;
												const dateLabel = startDate && endDate && startDate !== endDate ?
													`${dayjs(startDate).format("D MMM YYYY")} - ${dayjs(endDate).format("D MMM YYYY")}` :
													startDate ? dayjs(startDate).format("D MMM YYYY") : "";
												const eventId = event?.EventId || event?.Id;
												const eventLink = eventId ? `/events/${eventId}` : null;

												return (
													<Grid item xs={12} sm={6} md={4} key={eventId || eventName}>
														<Card
															outlined={true}
															px={0}
															py={0}
															style={{
																height: "100%",
																display: "flex",
																flexDirection: "column",
																borderRadius: 10,
																overflow: "hidden"
															}}>
															{eventImage && (
																<Image
																	alt={eventName}
																	noError={true}
																	noLoader={true}
																	src={eventImage}
																	style={{height: 160, width: "100%"}}
																	imgStyles={{width: "100%", height: "100%", display: "block"}} />
															)}
															<CardSection
																display="block"
																px={0}
																py={0}
																style={{flexGrow: 1, padding: 16}}>
																<Typography variant="subtitle1" sx={{fontWeight: 700}}>
																	{eventName}
																</Typography>
																{dateLabel && (
																	<Typography color="text.secondary" sx={{mb: 2}}>
																		{dateLabel}
																	</Typography>
																)}
																{eventLink && (
																	<Button label="Read more" uri={eventLink} size="small" />
																)}
															</CardSection>
														</Card>
													</Grid>
												);
											})}
										</Grid>
									)}
								</Stack>
							</CardSection>
						</Card>

						<Box id="timetable" />
						<Card px={0} py={0} style={{borderRadius: 12, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)"}}>
							<CardSection
								display="block"
								px={0}
								py={0}
								style={{padding: CARD_BODY_PADDING}}>
								<Stack spacing={3}>
									<Typography variant="h5" sx={{fontWeight: 800, color: "#e2b534"}}>
										Timetables
									</Typography>
									{timetablesError && (
										<Alert severity="error">We could not load timetables right now.</Alert>
									)}
									{timetableEntries.length ?
										(
											<CalendarView
												entries={timetableEntries.map(entry => ({
													...entry,
													onClick: entry.image ? () => setActiveTimetable(entry) : undefined
												}))}
												monthLabel={timetableMonth}
												onPrev={() => setTimetableMonth(prev => prev.subtract(1, "month"))}
												onNext={() => setTimetableMonth(prev => prev.add(1, "month"))}
											/>
										) :
										(
											<Typography color="text.secondary">
											There are no timetables available yet.
											</Typography>
										)}
								</Stack>
							</CardSection>
						</Card>

						<Divider ref={mapAnchorRef} />

						<Card px={0} py={0} style={{borderRadius: 12, boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)"}}>
							<CardSection
								display="block"
								px={0}
								py={0}
								style={{padding: CARD_BODY_PADDING}}>
								<Typography variant="h6" sx={{fontWeight: 700, mb: 2}}>
									Railway map
								</Typography>
								<RaildaysMap
									markers={mapMarkers}
									bounds={mapMarkers.length ? mapMarkers.map(marker => marker.position) : []}
									center={mapMarkers.length ? mapMarkers[0].position : [54.5, -2]}
									zoom={12}
									markerShape="pin"
									height={420} />
							</CardSection>
						</Card>

						<Dialog
							open={Boolean(activeTimetable)}
							onClose={handleCloseActiveTimetable}
							fullScreen={true}
							fullWidth={true}
							noActions={true}
							noCloseButton={true}
							noContent={true}
							noFlex={true}
							PaperProps={{
								sx: {
									backgroundColor: "transparent",
									boxShadow: "none",
									overflow: "visible"
								}
							}}
						>
							<Box
								onClick={handleCloseActiveTimetable}
								sx={{
									position: "fixed",
									inset: 0,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									minHeight: "100vh",
									p: {xs: 2, md: 4},
									backgroundColor: "rgba(15, 23, 42, 0.56)"
								}}>
								<Box
									onClick={event => event.stopPropagation()}
									sx={{
										position: "relative",
										maxWidth: "calc(100vw - 48px)"
									}}>
									<IconButton
										icon={CloseIcon}
										label="Close timetable"
										onClick={handleCloseActiveTimetable}
										style={{
											position: "absolute",
											top: 8,
											right: 8,
											zIndex: 2,
											backgroundColor: "rgba(255,255,255,0.9)",
											border: "1px solid rgba(148, 163, 184, 0.5)"
										}} />

									{activeTimetable?.image && !activeTimetableLoadError && (
										<Box
											component="img"
											alt={activeTimetable?.title || "Timetable"}
											src={activeTimetable.image}
											onError={() => setActiveTimetableLoadError(true)}
											sx={{
												display: "block",
												maxWidth: "100%",
												maxHeight: "calc(100vh - 96px)",
												borderRadius: 1,
												boxShadow: "0 24px 50px rgba(15, 23, 42, 0.42)"
											}} />
									)}
									{(!activeTimetable?.image || activeTimetableLoadError) && (
										<Box
											sx={{
												minWidth: {xs: "calc(100vw - 64px)", md: 500},
												minHeight: 240,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												textAlign: "center",
												borderRadius: 2,
												border: "1px solid rgba(148, 163, 184, 0.5)",
												backgroundColor: "rgba(255, 255, 255, 0.82)",
												backdropFilter: "blur(6px)",
												boxShadow: "0 24px 50px rgba(15, 23, 42, 0.22)",
												px: 3
											}}>
											<Typography sx={{fontWeight: 600}}>
													Timetable view unavailable at this time.
											</Typography>
										</Box>
									)}
								</Box>
							</Box>
						</Dialog>
					</Stack>
				)}
			</Container>
		</Box>
	);
};

export default RailwayDetailView;
